import localizeContentMapper from '../../common/utils/localizeContentMapper';
import { generalContentDefaultConfiguration } from '../constants';
import React, { useEffect, useState } from 'react';
import GeneralContentLayout1 from './layout_01/GeneralContentLayout';
import GeneralContentLayout2 from './layout_02/GeneralContentLayout';
import GeneralContentLayout3 from './layout_03/GeneralContentLayout';
import { GAActionMappings, GAPageViewMappings, GARouteMappings } from '../../constants/gaMappings';
import { GeneralContentProps } from '../interfaces/generalContentProps';
import { RoutePageProps } from '../../base/components/Routes';
import { sendGeneralContentEventDetails, sendPageViewDetails } from '../../common/utils/gaEvents';
import { useBasicDetails } from '../../store/storeApi';
import { TemplateLayout, WorkflowItem } from '../../store/interfaces';
import { Language } from '../../common/interfaces/language';
import { WorkflowItemCode } from '../../constants';
import { useScheduledAssessmentTimer } from '../../taskList/hooks';
import { Task, TaskItem } from '../../taskList/interfaces/taskTile';
import useHotjar from '../../hotjar/useHotjar';
import TaskListProgressBar from '../../taskList/components/layout_02/TaskListProgressBar';
import { useMediaQuery, useTheme } from '@material-ui/core';
import GeneralContentLayout4 from './layout_04/GeneralContentLayout';
import GeneralContentLayout5 from './layout_05/GeneralContentLayout';
import { useAppSelector } from '../../redux-store/hook';
/** Map for layout and name */
const GcLayoutComponentsMap: { [key: string]: React.FC<GeneralContentProps> } = {
  LAYOUT_01: GeneralContentLayout1,
  LAYOUT_02: GeneralContentLayout2,
  LAYOUT_03: GeneralContentLayout3,
  LAYOUT_04: GeneralContentLayout4,
  LAYOUT_05: GeneralContentLayout5,
};

interface GeneralContentPageProps extends RoutePageProps {
  getNextWorkflowItem?(): Promise<void>;
  currentWorkflowItem?: WorkflowItem;
  isLanguageSelectionFormOpen?: boolean;
  returnToTaskList?: boolean;
  isSchedulablePackageAvailable?(): TaskItem | null;
  languages?: Language[];
  onLanguageSubmit?(selectedLanguage: Language): void;
  isNextButtonDisabled?: boolean;
  taskName?: string;
  tasks?: Task[];
  isNextItemAssessment?: boolean;
}

/** Component for GeneralContentPage */
const GeneralContentPage = (props: GeneralContentPageProps): JSX.Element => {
  const candidate = useAppSelector((state) => state.candidate);
  /** page view ga */
  useEffect(() => {
    sendPageViewDetails(GARouteMappings.GENCONTENT, GAPageViewMappings.GENCONTENT);
  }, []);

  const [pageStartTime, setPageStartTime] = useState<Date>(new Date());
  const template = useAppSelector((state) => state.template);
  const { layoutCode, settings } = template;
  const { isICPEnabled } = useAppSelector((state) => state.headerDetails);
  const { updateTimeLineSpinner } = useBasicDetails();

  const { currentWorkflowItem, taskName, tasks, isNextItemAssessment } = props;
  const parsedConfig =
    currentWorkflowItem && currentWorkflowItem.configuration && currentWorkflowItem.code === WorkflowItemCode.GENCONTENT
      ? JSON.parse(currentWorkflowItem.configuration).configWidgetJSON
      : generalContentDefaultConfiguration;
  const parsedConfiguration = currentWorkflowItem
    ? localizeContentMapper(parsedConfig, currentWorkflowItem.localizedContentMap)
    : parsedConfig;

  const schedulableTaskItem = props.isSchedulablePackageAvailable && props.isSchedulablePackageAvailable();
  const schedulableAssessment = useScheduledAssessmentTimer(schedulableTaskItem);
  const transformedConfigWidgetJSON = {
    ...parsedConfiguration,
    contentContainerBackgroundColor:
      parsedConfiguration.contentContainerBackgroundColor || settings.page.containerColor,
    backgroundColor: parsedConfiguration.backgroundColor || settings.page.backgroundColor,
    contentContainerOpacity: parsedConfiguration.contentContainerOpacity
      ? +parsedConfiguration.contentContainerOpacity
      : 100,
    buttonText: schedulableTaskItem ? schedulableAssessment.message : parsedConfig.buttonText,
  };

  const [loading, setLoading] = useState<boolean>(false);

  /** Method to save current and get next workflow item */
  const saveAndGetNextWorkflowItem = (video = ''): void => {
    // sending ga event
    let action = GAActionMappings.GENCONTENT;
    const images = transformedConfigWidgetJSON.bannerImageEnabled || transformedConfigWidgetJSON.backgroundImageEnabled;
    if (images && typeof video === 'string' && video !== '') {
      action = `${action} : Images & Video`;
    } else if (typeof video === 'string' && video !== '') {
      action = `${action} : Video`;
    } else if (images) {
      action = `${action} : Images`;
    }
    sendGeneralContentEventDetails(action, pageStartTime, transformedConfigWidgetJSON.name, taskName, {
      template,
      candidate,
    });

    /** get next wf item api call */
    if (props.getNextWorkflowItem) {
      updateTimeLineSpinner(true);
      setLoading(true);

      props.getNextWorkflowItem().finally(() => {
        setLoading(false);
        updateTimeLineSpinner(false);
        setPageStartTime(new Date());
      });
    }
  };
  const theme = useTheme();
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const { surveyEnabled, surveyTriggerCode } = transformedConfigWidgetJSON;

  useHotjar(surveyEnabled, surveyTriggerCode);

  return (
    <>
      {tasks && layoutCode === TemplateLayout.TEMPLATE_LAYOUT_02 && lgUp && (
        <TaskListProgressBar tasks={tasks} progressBar={transformedConfigWidgetJSON.progressBar}></TaskListProgressBar>
      )}
      {GcLayoutComponentsMap[transformedConfigWidgetJSON.layout] &&
        React.createElement(GcLayoutComponentsMap[transformedConfigWidgetJSON.layout], {
          ...transformedConfigWidgetJSON,
          isNextItemAssessment: isNextItemAssessment,
          loading,
          saveNGetNextWfItem: saveAndGetNextWorkflowItem,
          pageStartTime,
          isICPEnabled,
          schedulableTaskItem,
          isNextButtonDisabled: schedulableTaskItem ? schedulableAssessment.disabled : false,
          ...props,
        })}
    </>
  );
};

export default GeneralContentPage;
